import type { ReactNode } from 'react';

/** Пункт или группа меню оболочки. */
export interface IAppShellMenuItem {
    /**
     * Ключ пункта, уникальный во всём меню (обычно путь от корня): по нему активный пункт, раскрытые ветки и
     * закреплённые пункты; попадает в `data-node-key`.
     */
    id: string;
    /** Подпись; она же имя пункта для экранного диктора */
    label: string;
    /** Иконка корня (у корня без иконки — папка) или вложенного пункта */
    icon?: ReactNode;
    /**
     * Адрес: пункт — ссылка (`linkComponent` из `UiProvider`). У ветки с адресом щелчок раскрывает её, а
     * Ctrl/⌘-щелчок открывает адрес в новой вкладке; ветка с адресом, чьи дети ещё не загружены, открывает свой адрес.
     */
    href?: string;
    /** Вложенные пункты */
    children?: IAppShellMenuItem[];
    /** Ветка, у которой есть дети, даже если они ещё не загружены: раскрытие вызывает `loadChildren` меню */
    hasChildren?: boolean;
    /** Группа корня: между соседними корнями разных групп — разделитель */
    group?: string;
    /** Корень внизу панели, вне прокрутки (по умолчанию — в списке) */
    placement?: 'main' | 'footer';
    /** Пункт можно закрепить; по умолчанию — ссылка без детей */
    pinnable?: boolean;
}

/** Пункт в плоском списке меню: для поиска и закреплённых. */
export interface IAppShellMenuEntry {
    item: IAppShellMenuItem;
    /** Подписи предков */
    parents: string[];
    /** Ключи предков */
    ancestorIds: string[];
}

/** Ветка (есть дети или будут после загрузки). */
export const isMenuGroup = (item: IAppShellMenuItem) => Boolean(item.hasChildren || item.children?.length);

/** Ветка, дети которой ещё не загружены. */
export const isMenuBranchUnloaded = (item: IAppShellMenuItem) => Boolean(item.hasChildren && !item.children?.length);

export const canPinMenuItem = (item: IAppShellMenuItem) => item.pinnable ?? Boolean(item.href && !isMenuGroup(item));

export function flattenMenuItems(
    items: IAppShellMenuItem[],
    parents: string[] = [],
    ancestorIds: string[] = [],
): IAppShellMenuEntry[] {
    return items.flatMap((item) => [
        { item, parents, ancestorIds },
        ...flattenMenuItems(item.children ?? [], [...parents, item.label], [...ancestorIds, item.id]),
    ]);
}

/** Пункты, в пути которых (подписи предков и своя) есть все слова запроса. */
export function searchMenuItems(entries: IAppShellMenuEntry[], query: string) {
    const terms = query.trim().toLocaleLowerCase('ru').split(/\s+/);
    return entries.filter(({ item, parents }) => {
        const text = [...parents, item.label].join(' ').toLocaleLowerCase('ru');
        return terms.every((term) => text.includes(term));
    });
}

/** Ключи от корня до пункта `id` включительно. */
export function findMenuPath(items: IAppShellMenuItem[], id: string | undefined): string[] | undefined {
    if (id === undefined) return undefined;
    for (const item of items) {
        if (item.id === id) return [item.id];
        if (item.children?.length) {
            const path = findMenuPath(item.children, id);
            if (path) return [item.id, ...path];
        }
    }
    return undefined;
}
